import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Languages,
  Zap,
  Globe,
  Clock,
  Shield,
  Users,
  CheckCircle,
  ChevronDown,
  Star,
  Play,
  ArrowRight,
  Twitter,
  Github
} from 'lucide-react';

const Landing: React.FC = () => {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setFaqOpen(faqOpen === index ? null : index);
  };

  const features = [
    {
      icon: Languages,
      title: 'AI-Powered Translation',
      description: 'Neural machine translation with context awareness. Translates products, categories, and content with 95%+ accuracy.',
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      icon: Zap,
      title: 'One-Click Installation',
      description: 'Install on Shopify or WooCommerce in under 5 minutes. No coding required. Works with your existing theme.',
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      icon: Globe,
      title: '20+ Languages',
      description: 'Spanish, French, German, Chinese, Japanese, Arabic, and more. Expand to new markets instantly.',
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    },
    {
      icon: Clock,
      title: 'Real-Time Sync',
      description: 'Automatic translation of new products. Update once, translate everywhere. Keep all languages in sync.',
      color: 'text-orange-600',
      bg: 'bg-orange-100'
    },
    {
      icon: Shield,
      title: 'SEO Optimized',
      description: 'Translated meta titles, descriptions, and URLs. Rank in Google for every language you support.',
      color: 'text-red-600',
      bg: 'bg-red-100'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Review translations with your team. Built-in glossary and translation memory for consistency.',
      color: 'text-indigo-600',
      bg: 'bg-indigo-100'
    }
  ];

  const testimonials = [
    {
      quote: "Increased our international sales by 240% in just 3 months. The auto-translation feature saved us thousands in translation costs.",
      name: "Sarah Mitchell",
      role: "Fashion Boutique Owner",
      initials: "SM",
      color: "bg-blue-100 text-blue-600"
    },
    {
      quote: "Setup took less than 10 minutes. The Shopify integration is flawless. We're now selling in 8 countries!",
      name: "James Chen",
      role: "Electronics Store Owner",
      initials: "JC",
      color: "bg-green-100 text-green-600"
    },
    {
      quote: "Best ROI of any tool we've tried. Translation quality is exceptional and the support team is incredibly responsive.",
      name: "Maria Rodriguez",
      role: "Home Goods Retailer",
      initials: "MR",
      color: "bg-purple-100 text-purple-600"
    }
  ];

  const faqs = [
    {
      question: "How long does setup take?",
      answer: "Most customers are up and running in under 10 minutes. Simply install the app from the Shopify App Store or WordPress plugin directory, connect your account, and start translating."
    },
    {
      question: "What languages do you support?",
      answer: "We support 20+ languages including Spanish, French, German, Italian, Portuguese, Chinese (Simplified & Traditional), Japanese, Korean, Arabic, Russian, Dutch, Polish, Turkish, and more. New languages are added regularly based on customer demand."
    },
    {
      question: "How accurate are the translations?",
      answer: "Our AI-powered translation engine achieves 95%+ accuracy for most language pairs. We use neural machine translation with context awareness, and you can always review and edit translations through our intuitive dashboard. Pro and Enterprise plans include translation memory for improved consistency."
    },
    {
      question: "Can I translate my existing products?",
      answer: "Yes! Our bulk translation feature allows you to translate all existing products in just a few clicks. You can also set up auto-translation for new products, so everything stays in sync automatically."
    },
    {
      question: "Will this affect my SEO?",
      answer: "Actually, it will improve your SEO! We automatically translate meta titles, descriptions, and create localized URLs for each language. This helps you rank in Google for international markets. All translations are properly marked with hreflang tags."
    },
    {
      question: "What if I need to change a translation?",
      answer: "You have full control over all translations. Simply log into your dashboard, find the product or content, and edit the translation directly. Changes sync to your store instantly. You can also create custom glossaries to ensure brand terms are always translated correctly."
    },
    {
      question: "Do you offer refunds?",
      answer: "Yes! We offer a 30-day money-back guarantee. If you're not satisfied for any reason, contact our support team for a full refund. We also offer a 14-day free trial so you can test everything risk-free."
    },
    {
      question: "Can I upgrade or downgrade my plan?",
      answer: "Absolutely! You can upgrade or downgrade your plan at any time. When upgrading, you'll get immediate access to new features. When downgrading, changes take effect at the start of your next billing cycle."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="bg-background border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-primary">LocalizationHub</h1>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-muted-foreground hover:text-primary transition">Features</a>
              <Link to="/pricing" className="text-muted-foreground hover:text-primary transition">Pricing</Link>
              <a href="#faq" className="text-muted-foreground hover:text-primary transition">FAQ</a>
              <Link to="/login" className="text-muted-foreground hover:text-primary transition">Login</Link>
              <Button asChild>
                <Link to="/register">Start Free Trial</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/5 to-primary/10 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20">
                Trusted by 500+ E-commerce Stores
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Translate Your Store in <span className="text-primary">Minutes</span>, Not Months
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                AI-powered translation platform for Shopify and WooCommerce.
                Reach global customers with professional translations in 20+ languages.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="text-lg px-8 shadow-lg hover:shadow-xl">
                  <Link to="/register">
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8">
                  <a href="#demo">
                    <Play className="mr-2 h-5 w-5" />
                    Watch Demo
                  </a>
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span>14-day free trial</span>
                </div>
              </div>
            </div>
            <div className="hidden lg:block">
              <Card className="shadow-2xl">
                <CardContent className="p-8">
                  <img
                    src="https://via.placeholder.com/600x400/4F46E5/FFFFFF?text=Dashboard+Preview"
                    alt="LocalizationHub Dashboard"
                    className="rounded-lg w-full"
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-background py-12 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-muted-foreground font-semibold">Integrates seamlessly with</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
            <div className="text-4xl font-bold text-green-600">Shopify</div>
            <div className="text-4xl font-bold text-purple-600">WooCommerce</div>
            <div className="text-4xl font-bold text-blue-600">WordPress</div>
            <div className="text-4xl font-bold text-orange-600">Magento</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Everything You Need to Go Global</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Professional translation tools built specifically for e-commerce. No technical knowledge required.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className={`w-12 h-12 ${feature.bg} rounded-lg flex items-center justify-center mb-6`}>
                    <feature.icon className={`h-6 w-6 ${feature.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Stats */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-primary-foreground/80 text-lg">Active Stores</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">2M+</div>
              <div className="text-primary-foreground/80 text-lg">Translations</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">20+</div>
              <div className="text-primary-foreground/80 text-lg">Languages</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">98%</div>
              <div className="text-primary-foreground/80 text-lg">Customer Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Loved by Store Owners Worldwide</h2>
            <p className="text-xl text-muted-foreground">See what our customers are saying</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index}>
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic">"{testimonial.quote}"</p>
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mr-4 ${testimonial.color}`}>
                      {testimonial.initials}
                    </div>
                    <div>
                      <div className="font-semibold text-foreground">{testimonial.name}</div>
                      <div className="text-muted-foreground text-sm">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-muted-foreground">Choose the plan that fits your business</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Starter Plan */}
            <Card className="hover:border-primary transition-colors">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Starter</h3>
                  <p className="text-muted-foreground mb-6">Perfect for small stores</p>
                  <div className="text-5xl font-bold text-foreground mb-2">$49</div>
                  <div className="text-muted-foreground">per month</div>
                </div>
                <ul className="space-y-4 mb-8">
                  {['Up to 500 products', '3 languages', 'AI auto-translation', 'Email support', 'SEO optimization'].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="secondary" className="w-full">Start Free Trial</Button>
              </CardContent>
            </Card>

            {/* Pro Plan (Popular) */}
            <Card className="bg-primary text-primary-foreground relative transform scale-105 shadow-2xl">
              <Badge className="absolute top-0 right-0 bg-yellow-400 text-foreground rounded-bl-lg rounded-tr-lg rounded-tl-none rounded-br-none">
                MOST POPULAR
              </Badge>
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-2">Pro</h3>
                  <p className="text-primary-foreground/80 mb-6">For growing businesses</p>
                  <div className="text-5xl font-bold mb-2">$99</div>
                  <div className="text-primary-foreground/80">per month</div>
                </div>
                <ul className="space-y-4 mb-8">
                  {['Up to 5,000 products', '10 languages', 'AI auto-translation', 'Priority support', 'Translation memory', 'Team collaboration', 'Custom glossaries'].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle className="h-6 w-6 text-white mr-3 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="secondary" className="w-full bg-white text-primary hover:bg-gray-100">
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>

            {/* Enterprise Plan */}
            <Card className="hover:border-primary transition-colors">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Enterprise</h3>
                  <p className="text-muted-foreground mb-6">For large operations</p>
                  <div className="text-5xl font-bold text-foreground mb-2">$199</div>
                  <div className="text-muted-foreground">per month</div>
                </div>
                <ul className="space-y-4 mb-8">
                  {['Unlimited products', 'Unlimited languages', 'Dedicated account manager', '24/7 phone support', 'Custom integrations', 'SLA guarantee', 'API access'].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full">Contact Sales</Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-muted-foreground">All plans include 14-day free trial. No credit card required.</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground">Everything you need to know about LocalizationHub</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-muted/50 transition"
                >
                  <span className="text-lg font-semibold text-foreground">{faq.question}</span>
                  <ChevronDown
                    className={`w-6 h-6 text-muted-foreground transition-transform ${faqOpen === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {faqOpen === index && (
                  <div className="px-6 pb-5 text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-6">
            Ready to Go Global?
          </h2>
          <p className="text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto">
            Join 500+ store owners who are already selling internationally with LocalizationHub
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="text-lg px-10 shadow-xl">
              <Link to="/register">Start Free 14-Day Trial</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-10 border-white text-white hover:bg-white hover:text-primary">
              <a href="#demo">Schedule a Demo</a>
            </Button>
          </div>
          <p className="text-primary-foreground/80 mt-6">No credit card required • Setup in 10 minutes • Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-muted py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white font-bold text-lg mb-4">LocalizationHub</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                AI-powered translation platform for e-commerce stores. Reach global customers effortlessly.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-muted-foreground hover:text-white transition">Features</a></li>
                <li><a href="#pricing" className="text-muted-foreground hover:text-white transition">Pricing</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-white transition">Integrations</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-white transition">API</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="text-muted-foreground hover:text-white transition">Documentation</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-white transition">Help Center</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-white transition">Blog</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-white transition">Case Studies</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="text-muted-foreground hover:text-white transition">About Us</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-white transition">Contact</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-white transition">Privacy Policy</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-white transition">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-muted-foreground/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-muted-foreground text-sm">© 2025 LocalizationHub. All rights reserved.</p>
            <div className="flex gap-6 mt-4 md:mt-0">
              <a href="#" className="text-muted-foreground hover:text-white transition">
                <Twitter className="w-6 h-6" />
              </a>
              <a href="#" className="text-muted-foreground hover:text-white transition">
                <Github className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
